import { AxiosRequestConfig, AxiosResponse } from 'axios';
import ThrottledApi from './api/throttledApi';
import { STORE_ENDPOINTS } from '../features/store/constants';
import {
  Store,
  CreateStoreFormData,
  UpdateStoreFormData,
  StoreResponse,
  CloverConnectionStatus,
  CloverConnectResponse,
  CloverDisconnectResponse,
  CloverSyncResponse,
  StoreLocation,
} from '../features/store/types';

export interface VerifoneAgentConfigUpdate {
  PosDataUpload?: {
    Enabled: boolean;
  };
  PosDataDownload?: {
    Enabled: boolean;
  };
  Verifone: {
    BaseUrl?: string;
    User?: string;
    Password?: string;
    UseCompressed?: boolean;
    AllowInsecure?: boolean;
    NoProxy?: boolean;
    TimeoutSeconds?: number;
  };
}

export interface PosCredentialsSummary {
  store_id: string;
  pos_type: string | null;
  pos_display_name: string;
  username: string | null;
}

// ===== GET OPERATIONS =====

export const getAllStores = async (getAll: boolean = false): Promise<Store[]> => {
  try {
    const response = await ThrottledApi.get(
      `${STORE_ENDPOINTS.GET_ALL_STORES}${getAll ? '?get_all=true' : ''}`,
      {}
    ) as AxiosResponse;
    if (response?.data?.success && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    console.log('StoreService: No stores found', response?.data);
    return response?.data || [];
  } catch (error) {
    console.error('StoreService: Failed to fetch all stores:', error);
    throw error;
  }
};

export const getStoreById = async (storeId: string): Promise<Store> => {
  try {
    const response = await ThrottledApi.get(STORE_ENDPOINTS.GET_STORE_BY_ID(storeId), {}) as AxiosResponse;
    if (response.data) {
      return response?.data;
    }
    throw new Error('Store not found');
  } catch (error) {
    console.error('StoreService: Failed to fetch store by ID:', error);
    throw error;
  }
};

export const getPosCredentialsSummary = async (
  storeId: string
): Promise<PosCredentialsSummary> => {
  const response = await ThrottledApi.get(
    STORE_ENDPOINTS.GET_POS_CREDENTIALS_SUMMARY(storeId),
    { suppressErrorToast: true } as AxiosRequestConfig
  ) as AxiosResponse<PosCredentialsSummary>;
  return response.data;
};

export const getStoreLocations = async (
  storeId: string
): Promise<StoreLocation[]> => {
  const response = await ThrottledApi.get(
    STORE_ENDPOINTS.GET_STORE_LOCATIONS(storeId),
    { suppressErrorToast: true } as AxiosRequestConfig
  ) as AxiosResponse<StoreLocation[]>;
  return response.data;
};

export const getCloverConnectionStatus = async (
  storeId: string
): Promise<CloverConnectionStatus> => {
  const response = await ThrottledApi.get(
    STORE_ENDPOINTS.GET_CLOVER_STATUS(storeId),
    {}
  ) as AxiosResponse<CloverConnectionStatus>;
  return response.data;
};

export const connectClover = async (
  storeId: string
): Promise<CloverConnectResponse> => {
  const response = await ThrottledApi.post(
    STORE_ENDPOINTS.CONNECT_CLOVER(storeId),
    {},
    {}
  ) as AxiosResponse<CloverConnectResponse>;
  return response.data;
};

export const disconnectClover = async (
  storeId: string
): Promise<CloverDisconnectResponse> => {
  const response = await ThrottledApi.delete(
    STORE_ENDPOINTS.DISCONNECT_CLOVER(storeId),
    {}
  ) as AxiosResponse<CloverDisconnectResponse>;
  return response.data;
};

export const syncClover = async (
  storeId: string
): Promise<CloverSyncResponse> => {
  const response = await ThrottledApi.post(
    STORE_ENDPOINTS.SYNC_CLOVER(storeId),
    {},
    {}
  ) as AxiosResponse<CloverSyncResponse>;
  return response.data;
};

// ===== POST/UPDATE OPERATIONS =====

export const createStore = async (storeData: CreateStoreFormData): Promise<StoreResponse> => {
  try {
    console.log('🏪 StoreService: Creating store with data:', storeData);
    const response = await ThrottledApi.post(STORE_ENDPOINTS.CREATE_STORE, storeData, {}) as AxiosResponse;

    if (response?.data) {
      console.log('🏪 StoreService: Store created successfully');
      return {
        success: true,
        data: response.data,
      };
    }

    console.log('🏪 StoreService: Store creation failed:', response?.data?.error);
    return {
      success: false,
      error: response?.data?.error || 'Failed to create store',
    };
  } catch (error) {
    console.error('StoreService: Store creation error:', error);
    throw error;
  }
};

export const updateStore = async (storeId: string, storeData: UpdateStoreFormData): Promise<StoreResponse> => {
  try {
    console.log('🏪 StoreService: Updating store', storeId, 'with data:', storeData);
    const response = await ThrottledApi.put(STORE_ENDPOINTS.UPDATE_STORE(storeId), storeData, {}) as AxiosResponse;

    if (response?.data) {
      console.log('🏪 StoreService: Store updated successfully');
      return {
        success: true,
        data: response.data.data,
      };
    }

    console.log('🏪 StoreService: Store update failed:', response?.data?.error);
    return {
      success: false,
      error: response?.data?.error || 'Failed to update store',
    };
  } catch (error) {
    console.error('StoreService: Store update error:', error);
    throw error;
  }
};

export const updateAgentConfig = async (
  storeId: string,
  configData: VerifoneAgentConfigUpdate
): Promise<void> => {
  await ThrottledApi.patch(`/v1/agent/config/${storeId}`, configData, {}) as AxiosResponse;
};

export const deleteStore = async (storeId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('🏪 StoreService: Deleting store:', storeId);
    const response = await ThrottledApi.delete(STORE_ENDPOINTS.DELETE_STORE(storeId), {}) as AxiosResponse;

    if (response?.data?.success) {
      console.log('🏪 StoreService: Store deleted successfully');
      return { success: true };
    }

    console.log('🏪 StoreService: Store deletion failed:', response?.data?.error);
    return {
      success: false,
      error: response?.data?.error || 'Failed to delete store',
    };
  } catch (error) {
    console.error('StoreService: Store deletion error:', error);
    throw error;
  }
};

export const uploadStoreLogo = async (storeId: string, logoData: FormData): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    console.log('🏪 StoreService: Uploading logo for store:', storeId);
    const response = await ThrottledApi.post(STORE_ENDPOINTS.UPLOAD_LOGO(storeId), logoData, {}) as AxiosResponse;
    
    if (response?.data) {
      return {
        success: true,
        data: response.data,
      };
    }

    return {
      success: false,
      error: response?.data?.error || 'Failed to upload logo',
    };
  } catch (error) {
    console.error('StoreService: Logo upload error:', error);
    throw error;
  }
};

export const getStoreLogo = async (logoUrl: string): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    console.log('🏪 StoreService: Getting store logo from:', logoUrl);
    const response = await ThrottledApi.get(logoUrl.replace('/api', ''), { responseType: 'blob' }) as AxiosResponse;
    
    if (response?.data) {
      console.log('🏪 StoreService: Logo retrieved successfully');
      return {
        success: true,
        data: response?.data,
        error: undefined
      };
    }
    
    console.log('🏪 StoreService: Logo retrieval failed');
    return {
      success: false,
      error: 'Failed to retrieve logo'
    };
  } catch (error) {
    console.error('StoreService: Failed to fetch store logo:', error);
    return {
      success: false,
      error: 'Failed to fetch store logo'
    };
  }
};

// Export all store functions as a single object for convenience
export const storeService = {
  getAllStores,
  getStoreById,
  getPosCredentialsSummary,
  getStoreLocations,
  createStore,
  updateStore,
  updateAgentConfig,
  deleteStore,
  uploadStoreLogo,
  getStoreLogo,
  getCloverConnectionStatus,
  connectClover,
  disconnectClover,
  syncClover,
};

export default storeService;
